import { useRef, useState } from "react"
import type { MouseEvent } from "react"
import { Box, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFileCsv, faPrint } from "@fortawesome/pro-light-svg-icons"

import { StandardCalendarButton } from "../../../../../shared/components/buttons/StandardCalendarButton"
import { FilterSearch } from "../../../../../shared/components/createOrder/components/FilterSearch"
import { StandardCountFilterChip } from "../../../../../shared/components/filters/StandardCountFilterChip"
import {
    MeetingAudienceFilterType,
    MeetingOrderFilterType,
    meetingOrderFilterButtonConfig,
} from "../../../../../shared/components/filters/orderTypeFilter/helpers"
import { StandardOrderTypeFilter } from "../../../../../shared/components/filters/orderTypeFilter/StandardOrderTypeFilter"
import { StandardSquareFilter } from "../../../../../shared/components/filters/squareFilter/StandardSquareFilter"
import { HIDDEN_IN_PRINT } from "../../../../../shared/components/print/printPageOnly"
import type { DtDateRange } from "../../../../../shared/components/table/StandardDateRangeFilter"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"

import { RESOURCE_TYPE_FILTERS } from "../upcomingMeetingsTypes"
import type { ResourceTypeFilter, UpcomingMeetingsPeriod } from "../upcomingMeetingsTypes"
import { UpcomingMeetingsDateFilter } from "./UpcomingMeetingsDateFilter"

const MEETING_AUDIENCE_FILTER_OPTIONS = Object.values(MeetingAudienceFilterType)

const useStyles = makeStyles()((theme) => ({
    toolbar: HIDDEN_IN_PRINT,
    orderTypeFilter: {
        width: "auto",
    },
    searchWrap: {
        "& .MuiOutlinedInput-root.MuiOutlinedInput-root": {
            boxShadow: "none",
            background: theme.palette.common.white,
        },
    },
    audience: {
        marginLeft: "auto",
    },
    audienceLabel: {
        color: theme.palette.tailwind.slate[500],
    },
}))

type Props = Readonly<{
    isCalendarActive: boolean
    isLoading: boolean
    period: UpcomingMeetingsPeriod
    customRange: DtDateRange
    orderType: MeetingOrderFilterType
    resourceType: ResourceTypeFilter
    resourceTypeCounts: Readonly<Record<ResourceTypeFilter, number>>
    audience: MeetingAudienceFilterType
    search: string
    onPresetSelect: (period: UpcomingMeetingsPeriod) => void
    onCustomRangeApply: (range: DtDateRange) => void
    onOrderTypeChange: (orderType: MeetingOrderFilterType) => void
    onResourceTypeChange: (resourceType: ResourceTypeFilter) => void
    onAudienceChange: (audience: MeetingAudienceFilterType) => void
    onSearchChange: (search: string) => void
    onPrint: () => void
    onExport: () => void
    onShowCalendar: () => void
}>

export function UpcomingMeetingsToolbar({
    isCalendarActive,
    isLoading,
    period,
    customRange,
    orderType,
    resourceType,
    resourceTypeCounts,
    audience,
    search,
    onPresetSelect,
    onCustomRangeApply,
    onOrderTypeChange,
    onResourceTypeChange,
    onAudienceChange,
    onSearchChange,
    onPrint,
    onExport,
    onShowCalendar,
}: Props) {
    const { classes } = useStyles()
    const [exportAnchorEl, setExportAnchorEl] = useState<HTMLElement | null>(null)
    // printing while the menu fades out would put it on the sheet
    const afterExportMenuClosedRef = useRef<(() => void) | null>(null)

    const handleExportMenuOpen = (event: MouseEvent<HTMLElement>) => setExportAnchorEl(event.currentTarget)
    const handleExportMenuClose = () => setExportAnchorEl(null)

    const handlePrint = () => {
        afterExportMenuClosedRef.current = onPrint
        handleExportMenuClose()
    }

    const handleExport = () => {
        handleExportMenuClose()
        onExport()
    }

    const handleExportMenuExited = () => {
        afterExportMenuClosedRef.current?.()
        afterExportMenuClosedRef.current = null
    }

    return (
        <Stack gap="1rem" paddingBottom="1.5rem" className={classes.toolbar}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap="1rem" flexWrap="wrap">
                <Stack direction="row" alignItems="center" gap=".75rem" flexWrap="wrap">
                    {!isCalendarActive && (
                        <UpcomingMeetingsDateFilter
                            period={period}
                            customRange={customRange}
                            onPresetSelect={onPresetSelect}
                            onCustomRangeApply={onCustomRangeApply}
                        />
                    )}
                    <StandardOrderTypeFilter
                        isMarginBottom={false}
                        orderType={orderType}
                        setOrderType={onOrderTypeChange}
                        config={meetingOrderFilterButtonConfig}
                        ignoreSelection={isCalendarActive}
                        rootClassName={classes.orderTypeFilter}
                    />
                </Stack>
                <Stack direction="row" alignItems="center" gap=".625rem">
                    {!isCalendarActive && (
                        <>
                            <Box className={classes.searchWrap}>
                                <FilterSearch
                                    search={search}
                                    setSearch={onSearchChange}
                                    activeWidth="16.25rem"
                                    inactiveWidth="2.5rem"
                                    placeholderId="service-center-reports-upcoming-meetings-search-input"
                                />
                            </Box>
                            <StandardExportButton bordered disabled={isLoading} onClick={handleExportMenuOpen} />
                            <Menu
                                anchorEl={exportAnchorEl}
                                open={Boolean(exportAnchorEl)}
                                onClose={handleExportMenuClose}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                                TransitionProps={{ onExited: handleExportMenuExited }}
                            >
                                <MenuItem onClick={handlePrint}>
                                    <ListItemIcon>
                                        <FontAwesomeIcon icon={faPrint} />
                                    </ListItemIcon>
                                    <LocalizedStrict id="export-button-print">
                                        <ListItemText>Print</ListItemText>
                                    </LocalizedStrict>
                                </MenuItem>
                                <MenuItem onClick={handleExport}>
                                    <ListItemIcon>
                                        <FontAwesomeIcon icon={faFileCsv} />
                                    </ListItemIcon>
                                    <LocalizedStrict id="service-center-reports-upcoming-meetings-export-menu-csv">
                                        <ListItemText>Export to CSV</ListItemText>
                                    </LocalizedStrict>
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                    <StandardCalendarButton isActive={isCalendarActive} onClick={onShowCalendar} />
                </Stack>
            </Stack>

            {!isCalendarActive && (
                <Stack direction="row" alignItems="center" gap="1rem" flexWrap="wrap">
                    {orderType === MeetingOrderFilterType.RESOURCE_BOOKING && (
                        <Stack direction="row" alignItems="center" gap=".5rem" flexWrap="wrap">
                            {RESOURCE_TYPE_FILTERS.map((option) => (
                                <StandardCountFilterChip
                                    key={option}
                                    isSelected={option === resourceType}
                                    count={resourceTypeCounts[option]}
                                    isCountLoading={isLoading}
                                    onClick={() => onResourceTypeChange(option)}
                                >
                                    <LocalizedEnum
                                        base="service-center-reports-upcoming-meetings-resource-type-tab"
                                        enumValue={option}
                                    >
                                        <>{option}</>
                                    </LocalizedEnum>
                                </StandardCountFilterChip>
                            ))}
                        </Stack>
                    )}
                    <Stack direction="row" alignItems="center" gap=".625rem" className={classes.audience}>
                        <LocalizedStrict id="service-center-reports-upcoming-meetings-audience-filter-label">
                            <Typography variant="body2" className={classes.audienceLabel}>
                                Show
                            </Typography>
                        </LocalizedStrict>
                        <StandardSquareFilter
                            value={audience}
                            onChange={onAudienceChange}
                            options={MEETING_AUDIENCE_FILTER_OPTIONS}
                            translationBase="standard-order-type-filter-mode"
                        />
                    </Stack>
                </Stack>
            )}
        </Stack>
    )
}
